<template>
  <div
    class="z-40 flex items-center h-12 px-4 bg-white border-b border-gray-200 shadow-sm shrink-0 gap-x-4 sm:gap-x-6 sm:px-6 lg:px-6"
  >
    <Button
      variant="ghost"
      type="button"
      class="py-2.5 text-gray-700"
      @click="emit('toggle-sidebar', !props.isSidebarOpen)"
    >
      <span class="sr-only">Open sidebar</span>
      <Bars3Icon class="w-6 h-6" aria-hidden="true" />
    </Button>

    <!-- Separator -->
    <div class="w-px h-6 bg-gray-900/10" aria-hidden="true" />

    <div class="flex self-stretch flex-1 gap-x-4 lg:gap-x-6">
      <form class="relative flex flex-1" action="#" method="GET">
        <RouteBreadcrumb />
      </form>
      <div class="flex items-center gap-x-4 lg:gap-x-6">
        <Button variant="ghost" type="button" class="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
          <span class="sr-only">View notifications</span>
          <BellIcon class="w-6 h-6" aria-hidden="true" />
        </Button>

        <!-- Separator -->
        <div class="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10" aria-hidden="true" />

        <!-- Profile dropdown -->
        <Menu as="div" class="relative">
          <MenuButton class="-m-1.5 flex items-center p-1.5">
            <span class="sr-only">Open user menu</span>
            <img class="w-8 h-8 rounded-full bg-gray-50" :src="currentUser?.photoUrl" alt="" />
            <span class="hidden lg:flex lg:items-center">
              <span class="ml-4 text-sm font-semibold leading-6 text-gray-900" aria-hidden="true">{{
                currentUser?.displayName
              }}</span>
              <ChevronDownIcon class="w-5 h-5 ml-2 text-gray-400" aria-hidden="true" />
            </span>
          </MenuButton>
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <MenuItems
              class="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
            >
              <MenuItem v-for="item in topNavs" :key="item.name" v-slot="{ active }">
                <Button
                  variant="ghost"
                  :class="[
                    active ? 'bg-gray-50' : '',
                    'w-full justify-start px-3 py-1 text-sm leading-6 text-gray-900',
                  ]"
                  @click="onClickNavigation(item)"
                >
                  {{ item.name }}
                </Button>
              </MenuItem>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { Bars3Icon, BellIcon } from "@heroicons/vue/24/outline";
import { ChevronDownIcon } from "@heroicons/vue/20/solid";
import { signOut, getAuth } from "firebase/auth";

const emit = defineEmits<{
  (e: "toggle-sidebar", show: boolean): void;
}>();

const props = defineProps<{
  isSidebarOpen: boolean;
}>();

const { allowedRoutes, currentUser } = storeToRefs(useUserStore());

interface Navigation {
  name: string;
  value: string;
  href: string;
  icon: any;
}

const topNavs = [
  { name: "Your profile", value: "profile" },
  { name: "Sign out", value: "signout" },
];

function onClickNavigation(navigation: (typeof topNavs)[number]) {
  if (navigation.value === "signout") {
    const auth = getAuth();

    signOut(auth)
      .then(() => {
        window.location.href = `/login`;
      })
      .catch((error) => {
        console.error(error);
      });
  } else if (navigation.value === "profile") {
    navigateTo("/profile");
  }
}
</script>
